import {
  BaseFormLayout,
  StyledInputTextField,
  appFormUtil
} from 'modules';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography
} from '@mui/material';

import { IInitialRegisterationOFormState } from 'modules/components/layer35/RegisterAccountSection/types';



const __specialCharacters = [
  '^',
  '$',
  '*',
  '.',
  '[',
  ']',
  '{',
  '}',
  '(',
  ')',
  '?',
  '-',
  '"',
  '!',
  '@',
  '#',
  '%',
  '&',
  '/',
  '\\',
  ',',
  '>',
  '<',
  '\'',
  ':',
  ';',
  '|',
  '_',
  '~',
  '`',
  '+',
  '='
];



export type RegisterAccountFormProps = {
  formState: appFormUtil.InferReturn<IInitialRegisterationOFormState>['formState'];
  genFieldProps: appFormUtil.InferReturn<IInitialRegisterationOFormState>['genFieldProps'];
  dispatch: appFormUtil.InferReturn<IInitialRegisterationOFormState>['dispatch'];
  fields: (keyof appFormUtil.InferReturn<IInitialRegisterationOFormState>['formState'])[];
  handleRegisterUser: () => void;
  errMessage: string;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: RegisterAccountFormProps) => {

  const {
    password
  } = props.formState;

  const hasSpecialCharacter = __specialCharacters.some((char) => password.includes(char));

  return (
    <>
      {props.errMessage && (
        <Typography
          variant='body2'
          color='error'
        >
          {props.errMessage}
        </Typography>
      )}

      <BaseFormLayout.FC
        title='Create your Account'
        subTitle='Enter your information to create an account or claim your invitation.'
        handleSubmit={props.handleRegisterUser}
        nextButton={(
          <Button
            variant='contained'
            color='primary'
            type='submit'
            fullWidth
            disabled={!props.formState.agreedToTermsChecked}
          >
            {!props.formState.agreedToTermsChecked ? 'You must agree to the terms to continue' : 'Create Account'}
          </Button>
        )}
      >


        {props.fields.map((field, i) => (
          <StyledInputTextField.FC
            key={i}
            {...props.genFieldProps(field)}
          />
        ))}

        <Box sx={{
          alignSelf: 'stretch'
        }}
        >
          <FormControlLabel
            label={(
              <Typography>
                By checking this box you agree and accept our <a target='_blank' href='https://www.meetsherlock.ai/terms' rel='noreferrer'>service agreement</a> and <a target='_blank' href='https://www.meetsherlock.ai/privacy' rel='noreferrer'>privacy policy</a> terms.
              </Typography>
            )}
            control={(
              <Checkbox
                value={props.formState.agreedToTermsChecked}
                onChange={(ev, checked) => {
                  props.dispatch({
                    type: 'UPDATE',
                    payload: {
                      property: 'agreedToTermsChecked',
                      value: checked
                    }
                  });
                }}
              />
            )}
          />
        </Box>

        <Box sx={{
          alignSelf: 'stretch'
        }}
        >
          <Typography variant='h4'>
            Password requirements:
          </Typography>

          <ul>
            <li>
              <PasswordCriteria
                text='At least 8 characters'
                isSatisfied={password.length >= 8}
              />
            </li>

            <li>
              <PasswordCriteria
                text='Contains at least 1 number'
                isSatisfied={/\d+/.test(password)}
              />
            </li>

            <li>
              <PasswordCriteria
                text='Contains at least 1 special character. One of:'
                isSatisfied={hasSpecialCharacter}
              />

              <ul>

                <PasswordCriteria
                  text={__specialCharacters.join(' ')}
                  isSatisfied={hasSpecialCharacter}
                />
              </ul>
            </li>

            <li>
              <PasswordCriteria
                text='Contains at least 1 uppercase letter'
                isSatisfied={/[A-Z]+/.test(password)}
              />
            </li>

            <li>
              <PasswordCriteria
                text='Contains at least 1 lowercase letter'
                isSatisfied={/[a-z]+/.test(password)}
              />
            </li>
          </ul>
        </Box>
      </BaseFormLayout.FC>
    </>
  );
};



const PasswordCriteria = ({
  text, isSatisfied
}: {
  text: string;
  isSatisfied: boolean;
}) => {
  return (
    <Typography
      variant='formLabel'
      sx={{
        color: (theme) => isSatisfied ? theme.palette.primary.main : theme.palette.error.main
      }}
    >
      {text}
    </Typography>
  );
};
